<i18n>
{
  "fr": {
    "searchProperty": "Chercher une propiété",
    "createProperty": "Créer une propriété",
    "town": "Commune",
    "street": "Rue",
    "streetNumber": "Numéro de police",
    "noPropertiesFound": "Aucune propriété trouvé",
    "Properties": "Propriétés",
    "properties": "propriétés",
    "loading": "Chargement...",
    "folderNumber": "N° dossier",
    "filter": "Filtrer",
    "compliance_investigation_only": "Enquête de conformité seule"
  }
}
</i18n>

<template>
  <b-card body-class="pd-0" header-class="bg-transparent bd-b-0" class="card-hover card-suze">
    <template v-slot:header>
      <h6 class="card-title mg-b-0">{{ $t('Properties') }}</h6>
      <div class="d-flex">
        <b-button
          size="sm"
          v-b-toggle.user-filter-div
          class="s-btn btn-white mg-r-10"
          type="button"
          id="filter-button"
        >
          <IconFilter class="suzv" />
          <span class="hidden-xs button-filter">
            {{$t('filter')}}
            <i class="fa fa-angle-down fa-lg"></i>
          </span>
          <span class="visible-xs">
            <i class="fa fa-filter"></i>
          </span>
        </b-button>
        <router-link
          tag="a"
          class="s-btn btn-cta s-btn pull-right"
          :to="{ name: 'property_create' }"
        >
          <i class="fa fa-plus-circle fa-lg"></i>
          {{ $t('createProperty') }}
        </router-link>
      </div>
    </template>
    <b-collapse id="user-filter-div" class="animate-show filter-bloc">
      <div class="card-header">
        <Search />
      </div>
    </b-collapse>
    <table class="table table-striped admin-list">
      <thead>
        <tr class="thead-color">
          <th scope="col" @click="sort('folderNumber')">
            {{ $t('folderNumber') }}
            <i
              v-if="sortIcon('folderNumber', 'DESC')"
              class="fa fa-sort-desc"
              aria-hidden="true"
            ></i>
            <i v-if="sortIcon('folderNumber', 'ASC')" class="fa fa-sort-asc" aria-hidden="true"></i>
          </th>
          <th scope="col" @click="sort('town')">
            {{ $t('town') }}
            <i
              v-if="sortIcon('town', 'DESC')"
              class="fa fa-sort-desc"
              aria-hidden="true"
            ></i>
            <i v-if="sortIcon('town', 'ASC')" class="fa fa-sort-asc" aria-hidden="true"></i>
          </th>
          <th scope="col" @click="sort('street')">
            {{ $t('street') }}
            <i
              v-if="sortIcon('street', 'DESC')"
              class="fa fa-sort-desc"
              aria-hidden="true"
            ></i>
            <i v-if="sortIcon('street', 'ASC')" class="fa fa-sort-asc" aria-hidden="true"></i>
          </th>
          <th scope="col" @click="sort('numberStreet')">
            {{ $t('streetNumber') }}
            <i
              v-if="sortIcon('streetNumber', 'DESC')"
              class="fa fa-sort-desc"
              aria-hidden="true"
            ></i>
            <i v-if="sortIcon('complianceSurveyOnly', 'ASC')" class="fa fa-sort-asc" aria-hidden="true"></i>
          </th>
          <th scope="col" @click="sort('complianceSurveyOnly')">
            {{ $t('compliance_investigation_only') }}
            <i
              v-if="sortIcon('complianceSurveyOnly', 'DESC')"
              class="fa fa-sort-desc"
              aria-hidden="true"
            ></i>
            <i v-if="sortIcon('complianceSurveyOnly', 'ASC')" class="fa fa-sort-asc" aria-hidden="true"></i>
          </th>
        </tr>
      </thead>
      <tbody>
        <router-link
          tag="tr"
          v-for="property in properties"
          :key="property.id"
          class="request_row"
          :to="{ name: 'property_detail', params: { propertyId: property.id } }"
        >
          <td>{{ property.folderNumber }}</td>
          <td>{{ getFullTextTown(property.address.town).toUpperCase() }}</td>
          <td>{{ property.address.street }}</td>
          <td>{{ property.address.numberStreet }}</td>
          <td class="text-capitalize">{{ property.complianceSurveyOnly }}</td>
        </router-link>
      </tbody>
    </table>
    <b-row v-if="!loaderData">
      <b-col md="12">
        <paginate
          :page-count="getPageCount"
          :click-handler="getPropertiesByPage"
          :prev-text="'«'"
          :next-text="'»'"
          container-class="pagination"
        ></paginate>
      </b-col>
    </b-row>
    <div class="card-footer bg-transparent">
      <div v-if="properties.length == 0" class="account-error-div">
        <template v-if="loaderData">
          <strong>{{$t('loading')}}</strong>
          <b-spinner class="ml-auto"></b-spinner>
        </template>
        <template v-else>
          <v-icon name="info-circle" />
          <p>{{ $t('noPropertiesFound') }}</p>
        </template>
      </div>
    </div>
  </b-card>
</template>

<script>
import IconFilter from '../icons/IconFilter.vue';
import Search from '../Search';
import { mapState, mapGetters, mapActions } from 'vuex';

export default {
  name: 'properties',
  components: {
    Search,
    IconFilter,
  },
  data: () => ({
    loaderData: true,
    order: 'DESC',
    sorterFields: '',
  }),
  computed: {
    ...mapState({ properties: state => state.refProperties.properties, towns: state => state.refProperties.towns }),
    ...mapGetters(['getNotif', 'getPageCount']),
  },
  created: function() {
    this.order = 'DESC';
    this.sorterFields = { sortField: 'folderNumber', order: this.order };
    this.getProperties();
    this.fetchTowns();
  },
  methods: {
    ...mapActions(['fetchTowns']),
    sortIcon(fieldName, order) {
      return this.sorterFields.sortField == fieldName && this.order == order;
    },
    getFullTextTown(townCode) {
      let town = this.towns.find(town => town.code == townCode);
      if (town) {
        let name = town.internalLabel.toLowerCase();
        townCode = `${name.charAt(0).toUpperCase()}${name.slice(1)}`;
      }
      return townCode;
    },
    getProperties() {
      this.$store
        .dispatch('fetchProperties', { sortField: 'folderNumber', order: this.order })
        .then(() => (this.loaderData = false));
    },
    getPropertiesByPage(pageNum) {
      this.$store
        .dispatch('fetchProperties', {
          page: pageNum,
          sortField: this.sorterFields.sortField,
          order: this.sorterFields.order,
        })
        .then(() => (this.loaderData = false));
    },
    sort: function(fieldName) {
      this.order == 'ASC' ? (this.order = 'DESC') : (this.order = 'ASC');
      this.sorterFields = { sortField: fieldName, order: this.order };
      this.$store
        .dispatch('fetchProperties', { sortField: fieldName, order: this.order })
        .then(() => (this.loaderData = false));
    },
  },
};
</script>
